import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { unique } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { reportClientError } from 'services/errorReportingService'
import {
  buildAddBookingsMessage,
  type AddBookingsMessageInput,
  type AddBookingsOutcome,
} from 'tripPlanner/components/Chat/addBookingsMessage'
import { useUpdateChatMutation } from 'tripPlanner/hooks/api/chat'
import { useChatOperationScope } from './useChatOperationScope'

interface AddBookingsSendOptions {
  orderIds: Array<string>
  bookingItemIds: Array<string>
}

export type AddBookingsSend = (message: string, options: AddBookingsSendOptions) => Promise<void>

interface Args {
  chatId: number | undefined
  isLoggedIn: boolean
  onAddBookings: AddBookingsSend
}

const NO_ORDER_IDS: ReadonlyArray<string> = []

/**
 * Links the bookings' orders to an existing chat, builds the add-bookings message and hands it
 * to `onAddBookings`. The orders are busy only while the message is built, not while it streams.
 */
export function useAddBookingsSubmit({ chatId, isLoggedIn, onAddBookings }: Args) {
  const intl = useIntl()
  const captureScope = useChatOperationScope(chatId, isLoggedIn)
  const { mutateAsync: updateChat } = useUpdateChatMutation()
  const [submittingOrderIds, setSubmittingOrderIds] = useState(NO_ORDER_IDS)

  const submitBookings = useCallback(async(input: AddBookingsMessageInput): Promise<AddBookingsOutcome> => {
    const isCurrent = captureScope()
    const orderIds = unique(input.selectedBookings.map(({ order }) => order.id))
    const bookingItemIds = unique(input.selectedBookings.map(({ bookingItemId }) => bookingItemId))
    // Tags errors so Datadog can tell linking failures from context build failures.
    let phase: 'update_chat' | 'build_context' = 'update_chat'
    let message: string | undefined

    setSubmittingOrderIds(orderIds)
    try {
      // The backend needs the orders linked to build their booking context. A send without a chat links them as it creates it.
      if (chatId !== undefined) {
        await updateChat({ chatId, orderIds })
      }
      phase = 'build_context'
      message = await buildAddBookingsMessage(intl, input)
    } catch(error) {
      reportClientError(error, {
        message: 'Failed to add bookings to the trip planner chat',
        phase,
        orderIds,
        bookingItemIds,
      })
    } finally {
      setSubmittingOrderIds(NO_ORDER_IDS)
    }

    if (!isCurrent()) {
      return 'cancelled'
    }
    if (!message) {
      return 'unavailable'
    }
    // The stream shows and logs a failed send; not awaited so the picker isn't busy for the whole reply.
    onAddBookings(message, { orderIds, bookingItemIds }).catch(noop)
    return 'sent'
  }, [captureScope, chatId, intl, onAddBookings, updateChat])

  return { submittingOrderIds, submitBookings }
}
